import React from 'react';
import successIcon from '../assets/images/icons/success.svg';
import { X } from "lucide-react";

// Define the variant types for the modal
type ModalVariant = 'loading' | 'success' | 'warning' | 'error';

interface SimpleModalProps {
  content: React.ReactNode;
  close: (value: boolean) => void;
  variant?: ModalVariant;
}

// Simple modal for status messages and loading states
export const SimpleModal = ({ 
  content, 
  close, 
  variant = 'loading'
}: SimpleModalProps) => {
  // Loading spinner component
  const LoadingSpinner = () => (
    <div className="spinner" style={{ 
      width: '24px', 
      height: '24px', 
      border: '3px solid rgba(0, 0, 0, 0.1)', 
      borderRadius: '50%', 
      borderTop: '3px solid #333', 
      animation: 'spin 1s linear infinite',
      marginRight: '8px'
    }}></div>
  );

  // Success icon component
  const SuccessIcon = () => (
    <img 
      src={successIcon} 
      alt="Success" 
      style={{ 
        width: '24px', 
        height: '24px',
        marginRight: '8px'
      }} 
    />
  );

  // Warning icon component
  const WarningIcon = () => (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginRight: '8px' }}
    >
      <path 
        d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11 15V17H13V15H11ZM11 7V13H13V7H11Z" 
        fill="#FFA500"
      />
    </svg>
  );

  // Error icon component
  const ErrorIcon = () => (
    <svg 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginRight: '8px' }}
    >
      <path 
        d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11 15V17H13V15H11ZM11 7V13H13V7H11Z" 
        fill="#e53935"
      />
    </svg>
  );

  // Function to render the appropriate icon based on variant
  const renderIcon = () => {
    switch (variant) {
      case 'loading':
        return <LoadingSpinner />;
      case 'success':
        return <SuccessIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return null;
    }
  };

  return (
    <div className="account-modal">
      <div className="account-modal__container">
        {variant === 'error' && (
        <div className="account-modal__header--no-lines-alert">
            <div className="account-modal__head--no-lines">
              <div className="account-modal__title-container">
              {/* Empty title container for consistent layout */}
            </div>
            <button className="account-modal__close" onClick={() => close(false)} aria-label="Close modal">
              <X size={20} />
              </button>
            </div>
          </div>
        )}
        
        <div className="account-modal__content" style={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {renderIcon()}
            {content}
          </div>
          
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
        
        {/* No footer, no buttons */}
      </div>
    </div>
  );
}; 